import type { KnowledgebaseFilters } from "@/components/feature/KnowledgebaseFilters"
import {
  DOCUMENT_TAGS_QUERY_KEY,
  knowledgeBaseCategoriesApi,
} from "@/hooks/useKnowledgeBaseCategories/useKnowledgeBaseCategories"
import type { KnowledgebaseFile } from "@/types/knowledgebase"
import type { DocumentTagsResponse } from "@/types/knowledgebaseCategories"
import { useQueries } from "@tanstack/react-query"
import { useMemo } from "react"

interface UseFilteredKnowledgebaseFilesParams {
  files: KnowledgebaseFile[]
  filters: KnowledgebaseFilters
  enableDocumentTagFiltering?: boolean
}

const getDocumentTagIdsByFileId = (
  files: KnowledgebaseFile[],
  documentTagsData: Array<DocumentTagsResponse | undefined>,
) => {
  return files.reduce<Record<string, string[]>>((accumulator, file, index) => {
    const categories = documentTagsData[index]?.categories ?? []
    accumulator[file.id] = categories.flatMap((category) =>
      category.tags.map((tag) => tag.tagId),
    )
    return accumulator
  }, {})
}

const matchesCreatedAtRange = (
  file: KnowledgebaseFile,
  filters: KnowledgebaseFilters,
) => {
  if (!filters.createdAtRange?.from && !filters.createdAtRange?.to) {
    return true
  }

  const createdAt = new Date(file.createdAt)

  if (filters.createdAtRange.from && createdAt < filters.createdAtRange.from) {
    return false
  }

  if (filters.createdAtRange.to && createdAt > filters.createdAtRange.to) {
    return false
  }

  return true
}

const formatLocalDateToIso = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, "0")
  const day = String(date.getDate()).padStart(2, "0")

  return `${year}-${month}-${day}`
}

const isExpired = (expiryDate: string | null) => {
  if (!expiryDate) {
    return false
  }

  return expiryDate < formatLocalDateToIso(new Date())
}

export const useFilteredKnowledgebaseFiles = ({
  files,
  filters,
  enableDocumentTagFiltering = true,
}: UseFilteredKnowledgebaseFilesParams) => {
  const hasActiveTagFilters = useMemo(
    () => Object.values(filters.tags).some((tagIds) => tagIds.length > 0),
    [filters.tags],
  )

  const { documentTagsData } = useQueries({
    queries:
      hasActiveTagFilters && enableDocumentTagFiltering
        ? files.map((file) => ({
            queryKey: [...DOCUMENT_TAGS_QUERY_KEY, { fileId: file.id }],
            queryFn: () => knowledgeBaseCategoriesApi.getDocumentTags(file.id),
            staleTime: 30_000,
          }))
        : [],
    combine: (results) => ({
      documentTagsData: results.map((result) => result.data),
    }),
  })

  const filteredAndSortedFiles = useMemo(() => {
    const searchQuery = filters.search.toLocaleLowerCase()
    const activeTagFilters = Object.entries(filters.tags).filter(
      ([, tagIds]) => tagIds.length > 0,
    )
    const documentTagIdsByFileId =
      hasActiveTagFilters && enableDocumentTagFiltering
        ? getDocumentTagIdsByFileId(files, documentTagsData)
        : {}

    const filteredFiles = files.filter((file) => {
      if (
        searchQuery &&
        !file.fileName.toLocaleLowerCase().includes(searchQuery)
      ) {
        return false
      }

      if (
        filters.autoTagStatus.length > 0 &&
        (!file.autoTagStatus ||
          !filters.autoTagStatus.includes(file.autoTagStatus))
      ) {
        return false
      }

      if (!matchesCreatedAtRange(file, filters)) {
        return false
      }

      if (filters.excludeExpiredDocs && isExpired(file.expiryDate)) {
        return false
      }

      if (activeTagFilters.length === 0) {
        return true
      }

      const fileTagIds = documentTagIdsByFileId[file.id] ?? file.tagIds ?? []

      return activeTagFilters.every(([, selectedTagIds]) => {
        return selectedTagIds.some((tagId) => fileTagIds.includes(tagId))
      })
    })

    return filteredFiles
  }, [
    documentTagsData,
    enableDocumentTagFiltering,
    files,
    filters,
    hasActiveTagFilters,
  ])

  const selectableFiles = useMemo(
    () => filteredAndSortedFiles.filter((file) => file.source !== "SHAREPOINT"),
    [filteredAndSortedFiles],
  )

  return {
    filteredAndSortedFiles,
    selectableFiles,
  }
}
